/* eslint-disable @typescript-eslint/no-explicit-any */
// ✅ FloatingNav.tsx
import React, { useEffect, useRef, useState, ReactNode, useCallback } from "react";
import Chatbox from '../../chat/ChatBox/ChatBox';
import './FloatingNav.css';
import CLINIC_CONFIG from '../../../config/clinicConfig';

// Types
type NavItem = {
  icon: string;
  label: ReactNode;
  href: string;
};

type Tips = {
  data: string[];
};

const chatTips: Tips = {
  data: [
    'I need to schedule an appointment',
    'What the Cost of root canal?',
    'What are your hours?',
    'What insurances do you take?',
    'What are your payment and financing options?'
  ]
};

const mobileNavItems: NavItem[] = [
  { icon: "fa-regular fa-calendar-check", label: <span>Schedule<br />Online</span>, href: "/book-appointment" },
  { icon: "fa-solid fa-phone", label: "Call", href: CLINIC_CONFIG.getTelLink() },
  { icon: "fa-regular fa-comment-dots", label: "Text", href: CLINIC_CONFIG.getSmsLink("Hello%20") },
  { icon: "fa-regular fa-envelope", label: "Email", href: CLINIC_CONFIG.getEmailLink() },
  { icon: "fa-solid fa-location-dot", label: "Directions", href: CLINIC_CONFIG.mapUrl },
];


const desktopNavItems: NavItem[] = [
  { icon: "fa-regular fa-calendar-check", label: "Schedule Online", href: "/book-appointment" },
  { icon: "fa-regular fa-comment-dots", label: "Text", href: CLINIC_CONFIG.getSmsLink("Hello%20") },
  { icon: "fa-solid fa-phone", label: "Call", href: CLINIC_CONFIG.getTelLink() },
  { icon: "fa-regular fa-envelope", label: "Email", href: CLINIC_CONFIG.getEmailLink() },
  { icon: "fa-solid fa-location-dot", label: "Directions", href: CLINIC_CONFIG.mapUrl },
];

declare global {
  interface Window {
    bootstrap: any;
  }
}

interface InteractiveChatboxProps {
  isDesktop: boolean;
  onClick: () => void;
  onVoiceClick: () => void;
}

const InteractiveChatbox: React.FC<InteractiveChatboxProps> = ({ isDesktop, onClick, onVoiceClick }) => {
  const [message, setMessage] = useState('');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex((prevIndex) =>
        prevIndex === chatTips.data.length - 1 ? 0 : prevIndex + 1
      );
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const sendMessage = () => {
    if (message.trim()) {
      onClick();
      (window as any).openChatModal(message);
      setMessage('');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleSend = (e: React.MouseEvent) => {
    e.stopPropagation();
    sendMessage();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && message.trim()) {
      sendMessage();
    }
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value);
  };

  // Handle click on the chatbox container
  const handleChatboxClick = (e: React.MouseEvent) => {
    // Don't open modal if clicking on input or button
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'BUTTON' || target.closest('button')) {
      return;
    }
    onClick();
  };

  return (
    <div
      className={`chatbox ${isDesktop ? 'desktop-chat-container' : ''}`}
      onClick={handleChatboxClick}
      style={{ cursor: 'pointer' }}
    >
      <img src={"/images/favicon.webp"} alt="Dentist In Concord AI Assistant" className="chatbox-logo" />
      <div className="chatbox-text">
        <p className="chat-title" >Hi! I'm Tooth fairy! Your AI Assistant Ask me anything</p>
        <input
          type="text"
          className={`chat-input ${isDesktop ? 'desktop-chat-input' : ''}`}
          placeholder={chatTips.data[placeholderIndex]}
          ref={inputRef}
          value={message}
          onChange={handleMessageChange}
          onKeyDown={handleKeyDown}
          onClick={(e) => { e.stopPropagation(); onClick(); }} // Open modal when clicking input
        />
      </div>
      <button
        type="button"
        className="chat-voice-btn"
        aria-label="Talk to ToothFairy"
        title="Talk to ToothFairy"
        onClick={(e) => { e.stopPropagation(); onVoiceClick(); }}
      >
        <i className="bi bi-soundwave"></i>
      </button>
      <button
        type="button"
        className="chat-send-btn"
        aria-label="Send message"
        onClick={handleSend}
      >
        <i className="fa-solid fa-paper-plane"></i>
      </button>
    </div>
  );
};

const FloatingNavComponent: React.FC = () => {
  const [initialMessage, setInitialMessage] = useState('');
  const [triggerVoiceCall, setTriggerVoiceCall] = useState(false);

  const openModal = useCallback((msg: string = '') => {
    setInitialMessage(msg);
    const modalEl = document.getElementById("chatModal");
    if (modalEl) {
      const modal = new window.bootstrap.Modal(modalEl);
      modal.show();
    }
  }, []);

  const handleChatboxClick = () => {
    openModal('');
  };

  const handleVoiceClick = useCallback(() => {
    setTriggerVoiceCall(true);
    openModal('');
  }, [openModal]);

  useEffect(() => {
    if (!window.bootstrap) {
      import('bootstrap').then(mod => { window.bootstrap = mod; });
    }
    (window as any).openChatModal = openModal;

    const modalEl = document.getElementById("chatModal");
    if (!modalEl) return;

    const cleanUpModal = () => {
      document.querySelectorAll('.modal-backdrop').forEach(b => b.remove());
      document.body.classList.remove('modal-open');
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('padding-right');
      setInitialMessage('');
      setTriggerVoiceCall(false);
    };

    const focusChatInput = () => {
      const chatInput = modalEl.querySelector<HTMLInputElement>('.chatbox-input');
      if (chatInput) chatInput.focus();
    };

    modalEl.addEventListener('hidden.bs.modal', cleanUpModal);
    modalEl.addEventListener('shown.bs.modal', focusChatInput);

    return () => {
      modalEl.removeEventListener('hidden.bs.modal', cleanUpModal);
      modalEl.removeEventListener('shown.bs.modal', focusChatInput);

      // If the component unmounts while the modal is open, dismiss it properly
      if (window.bootstrap) {
        const instance = window.bootstrap.Modal.getInstance(modalEl);
        if (instance) {
          instance.dispose();
        }
      }
      cleanUpModal();
    };
  }, [openModal]);

  return (
    <>
      {/* Fullscreen Bootstrap Modal — works on all devices */}
      <div
        className="modal fade"
        id="chatModal"
        tabIndex={-1}
        aria-labelledby="chatModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-fullscreen">
          <div
            className="modal-content glass-bg d-flex flex-column h-100"
            style={{
              backdropFilter: "blur(20px) saturate(180%)",
              WebkitBackdropFilter: "blur(20px) saturate(180%)",
              backgroundColor: "rgba(255, 255, 255, 0.15)"
            }}
          >
            <div className="modal-header border-0" style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(20px) saturate(180%)', WebkitBackdropFilter: 'blur(20px) saturate(180%)', borderBottom: '0.5px solid rgba(0,0,0,0.08)' }}>
              <h5 className="modal-title" id="chatModalLabel" style={{ color: '#1d1d1f', fontWeight: 600, letterSpacing: '-0.02em' }}>Tooth Fairy Chat</h5>
              <button
                type="button"
                className="btn-close ms-auto"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <Chatbox
              initialMessage={initialMessage}
              startVoiceCall={triggerVoiceCall}
              onVoiceCallStarted={() => setTriggerVoiceCall(false)}
            />
          </div>
        </div>
      </div>

      {/* Mobile Floating Container */}
      <div className="mobile-floating-container d-lg-none">
        <InteractiveChatbox isDesktop={false} onClick={handleChatboxClick} onVoiceClick={handleVoiceClick} />
        <nav className="mobile-nav-bar" aria-label="Quick actions">
          {mobileNavItems.map((item, i) => (
            <a
              key={i}
              href={item.href}
              className="mobile-nav-item"
              target={item.label === "Directions" ? "_blank" : undefined}
              rel={item.label === "Directions" ? "noopener noreferrer" : undefined}
            >
              <i className={item.icon} aria-hidden="true"></i>
              <span>{item.label}</span>
            </a>
          ))}
        </nav>
      </div>

      {/* Desktop Floating Nav */}
      <nav className="desktop-floating-nav d-none d-lg-flex" aria-label="Quick actions">
        <a href={desktopNavItems[0].href} className="desktop-nav-item">
          <i className={desktopNavItems[0].icon} aria-hidden="true"></i>
          <span>{desktopNavItems[0].label}</span>
        </a>
        <a href={desktopNavItems[1].href} className="desktop-nav-item">
          <i className={desktopNavItems[1].icon} aria-hidden="true"></i>
          <span>{desktopNavItems[1].label}</span>
        </a>
        <InteractiveChatbox isDesktop={true} onClick={handleChatboxClick} onVoiceClick={handleVoiceClick} />
        <a href={desktopNavItems[2].href} className="desktop-nav-item">
          <i className={desktopNavItems[2].icon} aria-hidden="true"></i>
          <span>{desktopNavItems[2].label}</span>
        </a>
        <a href={desktopNavItems[3].href} className="desktop-nav-item">
          <i className={desktopNavItems[3].icon} aria-hidden="true"></i>
          <span>{desktopNavItems[3].label}</span>
        </a>
        <a href={desktopNavItems[4].href} className="desktop-nav-item" target="_blank" rel="noopener noreferrer">
          <i className={desktopNavItems[4].icon} aria-hidden="true"></i>
          <span>{desktopNavItems[4].label}</span>
        </a>
      </nav>
    </>
  );
};

export default FloatingNavComponent;

